import type { Priority, ShortTaskOutput, TaskStatusOutput } from "@orsane/schema/tasks";
import type { UserOutput } from "@orsane/schema/users";
import { addDays, format, parseISO, subDays } from "date-fns";
import { Calendar as CalendarIcon, Check, CircleCheckBig, CircleDashed, ListTree, User, UserPen } from "lucide-react";
import { useMemo } from "react";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { priorities } from "@/lib/priority";
import { useTaskStatuses } from "@/queries/task-status-queries";
import type { FilterValue } from "@/types/filter";
import { TaskBadge } from "../TaskBadge";
import { Calendar } from "../ui/calendar";
import { Command, CommandGroup, CommandItem, CommandList } from "../ui/command";
import { Separator } from "../ui/separator";
import { TaskFilterPopover } from "./TaskFilterPopover";
import { UserFilterPopover } from "./UserFilterPopover";

export const filterOperators = {
    is: "is",
    not: "is not",
    gte: "is equal or greater than",
    lte: "is equal or less than",
    contains: "contains",
    before: "is before",
    after: "is after",
};

export const useTaskFilterValues = (): FilterValue[] => {
    const { data } = useTaskStatuses();
    const { formatDate } = useDateFormatter();

    return useMemo(
        () => [
            {
                type: "status",
                label: "Status",
                icon: CircleDashed,
                operators: ["is", "not"],
                valueLabel: (value: any) => {
                    const status = data?.find((s: TaskStatusOutput) => s.id === value);
                    return status ? status.name : "-";
                },
                selectComponent: ({ onChange, value }: { onChange: (value: any) => void; value: any }) => (
                    <Command>
                        <CommandList>
                            <CommandGroup>
                                {!!data &&
                                    data.map((item: TaskStatusOutput) => (
                                        <CommandItem
                                            key={item.id}
                                            value={item.id.toString()}
                                            onSelect={() => onChange(item.id)}
                                            className="relative pl-8"
                                        >
                                            {item.id === value && (
                                                <Check className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center text-zinc-900" />
                                            )}
                                            <TaskBadge status={item} hideTodo={false} />
                                        </CommandItem>
                                    ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                ),
            },
            {
                type: "priority",
                label: "Priority",
                icon: () => priorities.medium.icon,
                operators: ["is", "not", "gte", "lte"],
                valueLabel: (value: any) => {
                    return priorities[value as Priority]?.label || "-";
                },
                selectComponent: ({ onChange, value }: { onChange: (value: any) => void; value: any }) => (
                    <Command>
                        <CommandList>
                            <CommandGroup>
                                {Object.keys(priorities).map((priorityKey) => {
                                    const priority = priorities[priorityKey as Priority];
                                    return (
                                        <CommandItem
                                            value={priority.label}
                                            key={priority.label}
                                            // onClick={(e) => e.stopPropagation()}
                                            onSelect={() => {
                                                onChange(priorityKey as unknown as Priority);
                                            }}
                                            className="relative pl-8"
                                        >
                                            {priorityKey === value && (
                                                <Check className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center text-zinc-900" />
                                            )}
                                            {priority.icon}
                                            {priority.label}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                ),
            },
            {
                type: "assignee",
                label: "Assignee",
                icon: User,
                operators: ["is", "not"],
                valueLabel: (value: UserOutput[] | null) => {
                    return value ? value.map((v) => (v.id === "empty" ? "empty" : v.name)).join(", ") : "-";
                },
                selectComponent: ({ onChange, value }: { onChange: (value: any) => void; value: any }) => (
                    <UserFilterPopover showEmpty={true} onChange={onChange} value={value as UserOutput[] | null} />
                ),
            },
            {
                type: "createdBy",
                label: "Created by",
                icon: UserPen,
                operators: ["is", "not"],
                valueLabel: (value: UserOutput[] | null) => {
                    return value ? value.map((v) => v.name).join(", ") : "-";
                },
                selectComponent: ({ onChange, value }: { onChange: (value: any) => void; value: any }) => (
                    <UserFilterPopover showEmpty={false} onChange={onChange} value={value as UserOutput[] | null} />
                ),
            },
            {
                type: "due",
                label: "Due Date",
                icon: CalendarIcon,
                operators: ["is", "not", "gte", "lte"],
                valueLabel: (value: any) => {
                    return value ? formatDate(parseISO(value)) : "-";
                },
                selectComponent: ({ onChange, value }: { onChange: (value: any) => void; value: any }) => {
                    const handleSelect = (date: Date | undefined) => {
                        onChange(date ? format(date, "yyyy-MM-dd") : null);
                    };

                    return (
                        <div>
                            <Calendar
                                mode="single"
                                selected={value ? parseISO(value) : undefined}
                                defaultMonth={value ? parseISO(value) : undefined}
                                onSelect={handleSelect}
                            />
                            <Separator />
                            <Command value={value || "none"}>
                                <CommandList>
                                    <CommandGroup>
                                        <CommandItem onSelect={() => handleSelect(new Date())}>Today</CommandItem>
                                        <CommandItem onSelect={() => handleSelect(addDays(new Date(), 1))}>
                                            Tomorrow
                                        </CommandItem>
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </div>
                    );
                },
            },
            {
                type: "completedAt",
                label: "Completed at",
                icon: CircleCheckBig,
                operators: ["is", "not", "gte", "lte"],
                valueLabel: (value: any) => {
                    return value ? formatDate(parseISO(value)) : "-";
                },
                selectComponent: ({ onChange, value }: { onChange: (value: any) => void; value: any }) => {
                    const handleSelect = (date: Date | undefined) => {
                        onChange(date ? format(date, "yyyy-MM-dd") : null);
                    };

                    return (
                        <div>
                            <Calendar
                                mode="single"
                                selected={value ? parseISO(value) : undefined}
                                defaultMonth={value ? parseISO(value) : undefined}
                                onSelect={handleSelect}
                            />
                            <Separator />
                            <Command value={value || "none"}>
                                <CommandList>
                                    <CommandGroup>
                                        <CommandItem onSelect={() => handleSelect(new Date())}>Today</CommandItem>
                                        <CommandItem onSelect={() => handleSelect(subDays(new Date(), 1))}>
                                            Yesterday
                                        </CommandItem>
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </div>
                    );
                },
            },
            {
                type: "parentTask",
                label: "Parent task",
                icon: ListTree,
                operators: ["is", "not"],
                valueLabel: (value: ShortTaskOutput[] | null) => {
                    return value ? value.map((v) => `T-${v.number} ${v.title}`).join(", ") : "-";
                },
                selectComponent: ({ onChange, value }: { onChange: (value: any) => void; value: any }) => (
                    <TaskFilterPopover showEmpty={true} onChange={onChange} value={value as ShortTaskOutput[] | null} />
                ),
            },
        ],
        [data, formatDate]
    );
};
